/**
 * Passkey / WebAuthn sign-in against Cognito's USER_AUTH flow, implemented with
 * direct InitiateAuth / RespondToAuthChallenge calls to the regional Cognito IDP
 * endpoint (the amazon-cognito-identity-js v6 SDK in this app predates USER_AUTH
 * / WEB_AUTHN, so we speak the JSON-1.1 wire protocol ourselves).
 *
 * Flow:
 *   1. InitiateAuth  AuthFlow=USER_AUTH, PREFERRED_CHALLENGE=WEB_AUTHN
 *        -> ChallengeName WEB_AUTHN + CREDENTIAL_REQUEST_OPTIONS (a JSON string
 *           of PublicKeyCredentialRequestOptionsJSON)
 *   2. navigator.credentials.get(...) with those options
 *        -> a PublicKeyCredential assertion (on iOS this triggers Face ID with
 *           no extra code, which is why we never special-case the platform)
 *   3. RespondToAuthChallenge WEB_AUTHN with the serialised assertion
 *        -> AuthenticationResult { IdToken, AccessToken, RefreshToken, ExpiresIn }
 *
 * The tokens come back ALREADY in epoch-relative `ExpiresIn` form, so the caller
 * persists them through the same `nest.oauth.*` shape as every other path.
 *
 * If the account has no registered passkey (or the browser has no authenticator)
 * we surface a typed, non-silent PasskeyNotEnrolledError so the UI can prompt the
 * user toward email/password instead of failing opaquely.
 */

import { getStoredTokens, isExpired, refreshTokens } from '@/lib/oauth'

const USER_POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID as string | undefined
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined

/** Thrown when WebAuthn can't proceed for this account/browser (not a hard error). */
export class PasskeyNotEnrolledError extends Error {
  constructor(message = 'No passkey is enrolled for this account on this device.') {
    super(message)
    this.name = 'PasskeyNotEnrolledError'
    Object.setPrototypeOf(this, PasskeyNotEnrolledError.prototype)
  }
}

/** The token bundle a successful passkey sign-in yields (caller persists it). */
export interface PasskeyTokens {
  idToken: string
  accessToken: string
  refreshToken?: string
  /** Cognito's relative ExpiresIn (seconds) for the access token. */
  expiresInSeconds?: number
}

/** True only where the browser can actually attempt a WebAuthn assertion. */
export function isPasskeySupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.credentials &&
    typeof navigator.credentials.get === 'function'
  )
}

function regionFromPoolId(poolId: string): string {
  // Pool ids are "<region>_<suffix>", e.g. us-east-1_knEL7cqS3.
  return poolId.split('_')[0]
}

function idpEndpoint(): string {
  if (!USER_POOL_ID || !CLIENT_ID) {
    throw new Error('Missing VITE_COGNITO_USER_POOL_ID or VITE_COGNITO_CLIENT_ID')
  }
  return `https://cognito-idp.${regionFromPoolId(USER_POOL_ID)}.amazonaws.com/`
}

async function cognitoCall<T>(
  target: string,
  body: unknown,
  { mapNotEnrolled = true }: { mapNotEnrolled?: boolean } = {},
): Promise<T> {
  const resp = await fetch(idpEndpoint(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
  })
  const text = await resp.text()
  const json = text ? JSON.parse(text) : {}
  if (!resp.ok) {
    const type = (json.__type as string) || ''
    const msg = (json.message as string) || resp.statusText
    // Cognito's "user has no credential" / "no such challenge" maps to not-enrolled.
    if (mapNotEnrolled && /UserNotFound|ResourceNotFound|InvalidParameter.*WEB_AUTHN/i.test(type)) {
      throw new PasskeyNotEnrolledError(msg)
    }
    const err = new Error(msg)
    err.name = type || 'CognitoError'
    throw err
  }
  return json as T
}

// ── base64url <-> ArrayBuffer (WebAuthn JSON uses base64url everywhere) ──────

function b64urlToBuf(b64url: string): ArrayBuffer {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/')
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
  const bin = atob(padded)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return bytes.buffer
}

function bufToB64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let str = ''
  for (const b of bytes) str += String.fromCharCode(b)
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** Re-hydrate the JSON request options Cognito sends into real WebAuthn types. */
export function toRequestOptions(
  json: PublicKeyCredentialRequestOptionsJSON,
): PublicKeyCredentialRequestOptions {
  return {
    ...json,
    challenge: b64urlToBuf(json.challenge),
    allowCredentials: (json.allowCredentials ?? []).map((c) => ({
      ...c,
      id: b64urlToBuf(c.id),
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  } as PublicKeyCredentialRequestOptions
}

/** Serialise the navigator assertion into the JSON Cognito's CREDENTIAL expects. */
export function serializeAssertion(cred: PublicKeyCredential): string {
  const r = cred.response as AuthenticatorAssertionResponse
  return JSON.stringify({
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment ?? undefined,
    response: {
      authenticatorData: bufToB64url(r.authenticatorData),
      clientDataJSON: bufToB64url(r.clientDataJSON),
      signature: bufToB64url(r.signature),
      userHandle: r.userHandle ? bufToB64url(r.userHandle) : undefined,
    },
    clientExtensionResults: cred.getClientExtensionResults?.() ?? {},
  })
}

interface InitiateAuthResponse {
  ChallengeName?: string
  Session?: string
  ChallengeParameters?: { CREDENTIAL_REQUEST_OPTIONS?: string }
}

interface RespondResponse {
  AuthenticationResult?: {
    IdToken: string
    AccessToken: string
    RefreshToken?: string
    ExpiresIn?: number
  }
}

type WebAuthnJson = { CREDENTIAL_REQUEST_OPTIONS?: string }

/**
 * Run a full passkey sign-in for `email`. Returns the token bundle on success.
 * Throws PasskeyNotEnrolledError when WebAuthn isn't available or no credential
 * is registered, and a plain Error for genuine failures (cancelled, network).
 */
export async function signInWithPasskey(email: string): Promise<PasskeyTokens> {
  if (!CLIENT_ID) throw new Error('Missing VITE_COGNITO_CLIENT_ID')
  if (!isPasskeySupported()) {
    throw new PasskeyNotEnrolledError('This browser does not support passkeys.')
  }

  const init = await cognitoCall<InitiateAuthResponse>('InitiateAuth', {
    AuthFlow: 'USER_AUTH',
    ClientId: CLIENT_ID,
    AuthParameters: { USERNAME: email, PREFERRED_CHALLENGE: 'WEB_AUTHN' },
  })

  if (init.ChallengeName !== 'WEB_AUTHN') {
    throw new PasskeyNotEnrolledError(
      `Cognito did not offer a WebAuthn challenge (got ${init.ChallengeName ?? 'none'}).`,
    )
  }
  const optsJson = (init.ChallengeParameters as WebAuthnJson | undefined)
    ?.CREDENTIAL_REQUEST_OPTIONS
  if (!optsJson) throw new PasskeyNotEnrolledError('No credential request options returned.')

  const requestOptions = toRequestOptions(
    JSON.parse(optsJson) as PublicKeyCredentialRequestOptionsJSON,
  )

  let assertion: PublicKeyCredential | null
  try {
    assertion = (await navigator.credentials.get({
      publicKey: requestOptions,
    })) as PublicKeyCredential | null
  } catch (err) {
    // NotAllowedError = user cancelled or no matching authenticator.
    if (err instanceof Error && err.name === 'NotAllowedError') {
      throw new PasskeyNotEnrolledError('Passkey was cancelled or unavailable.')
    }
    throw err
  }
  if (!assertion) throw new PasskeyNotEnrolledError('No passkey assertion produced.')

  const responded = await cognitoCall<RespondResponse>('RespondToAuthChallenge', {
    ChallengeName: 'WEB_AUTHN',
    ClientId: CLIENT_ID,
    Session: init.Session,
    ChallengeResponses: {
      USERNAME: email,
      CREDENTIAL: serializeAssertion(assertion),
    },
  })

  const result = responded.AuthenticationResult
  if (!result?.IdToken || !result.AccessToken) {
    throw new Error('Passkey challenge did not return tokens.')
  }
  return {
    idToken: result.IdToken,
    accessToken: result.AccessToken,
    refreshToken: result.RefreshToken,
    expiresInSeconds: result.ExpiresIn,
  }
}

// ── Registration and management (phenom-backend#704) ────────────────────────
//
// An operator registers a passkey while signed in (password first), using the
// access token from that session; Cognito requires its
// aws.cognito.signin.user.admin scope, which every USER_SRP / USER_PASSWORD /
// USER_AUTH token carries. The pool sets user_verification = "required"
// (phenom-infra#388), so the authenticator must verify the person (biometric
// or device PIN) on every registration and sign-in.

/** True where the browser can create a passkey (registration needs `create`). */
export function isPasskeyRegistrationSupported(): boolean {
  return isPasskeySupported() && typeof navigator.credentials.create === 'function'
}

/** Re-hydrate Cognito's creation options JSON into real WebAuthn types. */
export function toCreationOptions(
  json: PublicKeyCredentialCreationOptionsJSON,
): PublicKeyCredentialCreationOptions {
  return {
    ...json,
    challenge: b64urlToBuf(json.challenge),
    user: { ...json.user, id: b64urlToBuf(json.user.id) },
    excludeCredentials: (json.excludeCredentials ?? []).map((c) => ({
      ...c,
      id: b64urlToBuf(c.id),
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  } as PublicKeyCredentialCreationOptions
}

/**
 * Ask the browser for the device's own authenticator (Face ID, Touch ID,
 * Windows Hello), not a security key or a phone-to-laptop QR handoff
 * (phenom-backend#707). WebAuthn carries one "user verified" bit and does not
 * say whether the check was a biometric or the device PIN, so no browser can
 * refuse the PIN fallback; this is as close to biometric-only as the web gets.
 */
export function deviceAuthenticatorOnly(
  opts: PublicKeyCredentialCreationOptions,
): PublicKeyCredentialCreationOptions {
  return {
    ...opts,
    authenticatorSelection: {
      ...opts.authenticatorSelection,
      authenticatorAttachment: 'platform',
      residentKey: 'required',
      requireResidentKey: true,
      userVerification: 'required',
    },
    hints: ['client-device'],
  } as PublicKeyCredentialCreationOptions
}

/** Serialise a new credential into the RegistrationResponseJSON Cognito expects. */
export function serializeRegistration(cred: PublicKeyCredential): Record<string, unknown> {
  const r = cred.response as AuthenticatorAttestationResponse
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment ?? undefined,
    response: {
      clientDataJSON: bufToB64url(r.clientDataJSON),
      attestationObject: bufToB64url(r.attestationObject),
      transports: r.getTransports?.() ?? [],
    },
    clientExtensionResults: cred.getClientExtensionResults?.() ?? {},
  }
}

/** One registered passkey, as Cognito's ListWebAuthnCredentials reports it. */
export interface PasskeyCredential {
  CredentialId: string
  FriendlyCredentialName?: string
  RelyingPartyId?: string
  AuthenticatorAttachment?: string
  AuthenticatorTransports?: string[]
  /** Epoch seconds. */
  CreatedAt?: number
}

/**
 * Register a passkey for the signed-in operator. Throws PasskeyNotEnrolledError
 * when the browser can't create one or the operator cancels, and a plain Error
 * for anything Cognito rejects.
 */
export async function registerPasskey(accessToken: string): Promise<void> {
  if (!isPasskeyRegistrationSupported()) {
    throw new PasskeyNotEnrolledError('This browser cannot create passkeys.')
  }
  const start = await cognitoCall<{
    CredentialCreationOptions?: PublicKeyCredentialCreationOptionsJSON
  }>('StartWebAuthnRegistration', { AccessToken: accessToken }, { mapNotEnrolled: false })
  if (!start.CredentialCreationOptions) {
    throw new Error('Cognito returned no passkey creation options.')
  }

  let cred: PublicKeyCredential | null
  try {
    cred = (await navigator.credentials.create({
      publicKey: deviceAuthenticatorOnly(toCreationOptions(start.CredentialCreationOptions)),
    })) as PublicKeyCredential | null
  } catch (err) {
    if (err instanceof Error && err.name === 'NotAllowedError') {
      throw new PasskeyNotEnrolledError('Passkey creation was cancelled.')
    }
    if (err instanceof Error && err.name === 'InvalidStateError') {
      throw new PasskeyNotEnrolledError('This device already holds a passkey for this account.')
    }
    throw err
  }
  if (!cred) throw new PasskeyNotEnrolledError('No passkey was created.')

  await cognitoCall(
    'CompleteWebAuthnRegistration',
    { AccessToken: accessToken, Credential: serializeRegistration(cred) },
    { mapNotEnrolled: false },
  )
}

/** List the signed-in operator's passkeys. */
export async function listPasskeys(accessToken: string): Promise<PasskeyCredential[]> {
  const out: PasskeyCredential[] = []
  let NextToken: string | undefined
  do {
    const page = await cognitoCall<{ Credentials?: PasskeyCredential[]; NextToken?: string }>(
      'ListWebAuthnCredentials',
      { AccessToken: accessToken, MaxResults: 20, ...(NextToken ? { NextToken } : {}) },
      { mapNotEnrolled: false },
    )
    out.push(...(page.Credentials ?? []))
    NextToken = page.NextToken
  } while (NextToken)
  return out
}

/** Remove one of the signed-in operator's passkeys. */
export async function deletePasskey(accessToken: string, credentialId: string): Promise<void> {
  await cognitoCall(
    'DeleteWebAuthnCredential',
    { AccessToken: accessToken, CredentialId: credentialId },
    { mapNotEnrolled: false },
  )
}

// ── Offer a passkey after a password sign-in (phenom-backend#707) ──────────

const PROMPT_FLAG = 'nest.passkeyPrompt'

/** Called after an email + password sign-in succeeds. */
export function markPasswordSignIn(): void {
  try {
    sessionStorage.setItem(PROMPT_FLAG, '1')
  } catch {
    // Storage blocked: the prompt simply doesn't appear; Settings still works.
  }
}

/** True once per password sign-in; clears the flag so it never repeats. */
export function takePasswordSignInFlag(): boolean {
  try {
    const set = sessionStorage.getItem(PROMPT_FLAG) === '1'
    sessionStorage.removeItem(PROMPT_FLAG)
    return set
  } catch {
    return false
  }
}

/** A current access token for the Cognito user APIs, refreshed if it expired. */
export async function currentAccessToken(): Promise<string> {
  let tokens = getStoredTokens()
  if (isExpired(tokens)) tokens = await refreshTokens()
  if (!tokens?.accessToken) throw new Error('Your session expired. Sign in again.')
  return tokens.accessToken
}
